import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { testimonialsAPI, Testimonial } from '../services/testimonials';
import TestimonialEmbed from '../components/TestimonialEmbed';
import AddTestimonialModal from '../components/AddTestimonialModal';
import Avatar from '../components/Avatar';
import Logo from '../components/Logo';
import { STRINGS } from '../i18n/strings';
import './Testimonials.css';

const en = (k: string) => (STRINGS.en as any)[k] || '';

// Official brand glyphs (Simple Icons paths), inherit button text color.
const TgIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

// Official TikTok logo (colored glitch: cyan + red offset behind a white note).
const TT_PATH =
  "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z";
const TtIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d={TT_PATH} fill="#25F4EE" transform="translate(-0.9 0.7)" />
    <path d={TT_PATH} fill="#FE2C55" transform="translate(0.9 -0.7)" />
    <path d={TT_PATH} fill="#FFFFFF" />
  </svg>
);

const CopyIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M3.9 12a3.1 3.1 0 0 1 3.1-3.1h4V7H7a5 5 0 0 0 0 10h4v-1.9H7A3.1 3.1 0 0 1 3.9 12zM17 7h-4v1.9h4a3.1 3.1 0 0 1 0 6.2h-4V17h4a5 5 0 0 0 0-10zM8 11h8v2H8z" />
  </svg>
);

type SharePlatform = 'tg' | 'tiktok' | 'copy';

const LIKED_KEY = 'lomi.testimonials.liked';
const loadLiked = (): Set<string> => {
  try { return new Set(JSON.parse(localStorage.getItem(LIKED_KEY) || '[]')); } catch { return new Set(); }
};
const saveLiked = (s: Set<string>) => {
  try { localStorage.setItem(LIKED_KEY, JSON.stringify(Array.from(s))); } catch { /* noop */ }
};

const Post: React.FC<{
  t: Testimonial;
  liked: boolean;
  onLike: () => void;
  onShare: (platform: SharePlatform) => void;
}> = ({ t, liked, onLike, onShare }) => (
  <article className="t-post">
    <header className="t-post-head">
      <Avatar url={t.author_avatar_url || undefined} name={t.author_name} size={46} />
      <div className="t-post-who">
        <div className="t-post-name">
          {t.author_name}
        </div>
        {t.author_detail && <div className="t-post-meta">{t.author_detail}</div>}
      </div>
    </header>

    <div className="t-post-body">
      {t.media_type === 'video_link' && t.external_url && (
        <TestimonialEmbed url={t.external_url} platform={t.external_platform} />
      )}
      {t.media_type === 'audio' && t.audio_url && (
        <audio className="t-post-audio" controls preload="none" src={t.audio_url} />
      )}
      {t.quote && <blockquote className="t-post-quote">{t.quote}</blockquote>}
      {t.rating ? <div className="t-post-stars">{'★'.repeat(t.rating)}</div> : null}
    </div>

    <footer className="t-post-actions">
      <button className={`t-act ${liked ? 'liked' : ''}`} onClick={onLike} aria-pressed={liked}>
        <span className="t-act-icon">{liked ? '❤️' : '🤍'}</span>
        {t.like_count > 0 && <span className="t-act-count">{t.like_count}</span>}
      </button>
      <div className="t-post-share">
        <button className="t-share-ico tg" onClick={() => onShare('tg')} aria-label="Share on Telegram"><TgIcon /></button>
        <button className="t-share-ico tt" onClick={() => onShare('tiktok')} aria-label="Share on TikTok"><TtIcon /></button>
        <button className="t-share-ico copy" onClick={() => onShare('copy')} aria-label="Copy link"><CopyIcon /></button>
      </div>
    </footer>
  </article>
);

const Testimonials: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const navigate = useNavigate();
  // Open links correctly whether on the web or inside the Telegram Mini App
  // (t.me links via openTelegramLink, others via openLink).
  const openLink = (url: string) => {
    const tg = (window as any).Telegram?.WebApp;
    if (embedded && tg) {
      if (/(?:t\.me|telegram\.me)\//.test(url) && tg.openTelegramLink) { tg.openTelegramLink(url); return; }
      if (tg.openLink) { tg.openLink(url); return; }
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const [items, setItems] = useState<Testimonial[]>([]);
  const [liked, setLiked] = useState<Set<string>>(loadLiked);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => { document.title = 'What students say about Lomi 🍋'; }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    testimonialsAPI
      .list({ limit: 500 })
      .then((res) => setItems(res.testimonials))
      .catch((e) => setError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleLike = (t: Testimonial) => {
    const isLiked = liked.has(t.id);
    const next = new Set(liked);
    isLiked ? next.delete(t.id) : next.add(t.id);
    setLiked(next); saveLiked(next);
    // optimistic count
    setItems((prev) => prev.map((x) =>
      x.id === t.id ? { ...x, like_count: Math.max(0, x.like_count + (isLiked ? -1 : 1)) } : x));
    testimonialsAPI.like(t.id, isLiked)
      .then((count) => setItems((prev) => prev.map((x) => x.id === t.id ? { ...x, like_count: count } : x)))
      .catch(() => { /* keep optimistic */ });
  };

  const shareUrl = `${window.location.origin}/testimonials`;
  const BOT_URL = 'https://t.me/Lomistudybot';
  const TITLE = 'What students say about Lomi 🍋';
  const quoteLine = (t: Testimonial) =>
    t.quote ? `“${t.quote}” — ${t.author_name}` : `${t.author_name}'s Lomi story`;
  // Full message (title + quote + both links) for copy/TikTok.
  const fullMessage = (t: Testimonial) =>
    `${TITLE}\n\n${quoteLine(t)}\n\n🌐 ${shareUrl}\n🤖 Try Lomi: ${BOT_URL}`;
  const flash = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 1900); };

  const doShare = (t: Testimonial, platform: SharePlatform) => {
    if (platform === 'tg') {
      const text = `${TITLE}\n\n${quoteLine(t)}\n\n🤖 Try Lomi: ${BOT_URL}`;
      openLink(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`);
    } else if (platform === 'tiktok') {
      // TikTok has no web share-intent — copy the caption so they can paste it into a TikTok post.
      navigator.clipboard.writeText(fullMessage(t)).then(() => flash('Copied — paste it on TikTok 🎵')).catch(() => {});
    } else {
      navigator.clipboard.writeText(fullMessage(t)).then(() => flash('Link copied')).catch(() => {});
    }
  };

  return (
    <div className={`t-page ${embedded ? 't-embedded' : ''}`}>
      {!embedded && (
        <header className="t-header">
          <button onClick={() => navigate('/')} className="t-back" aria-label="Back to home">← Home</button>
          <div className="t-brand" onClick={() => navigate('/')}><Logo height={30} /></div>
        </header>
      )}

      <main className="t-feed">
        <section className="t-hero">
          <h1>{en('testimonials.title')}</h1>
        </section>

        {/* No aggregate "since launch" stat band here. Reach/engagement totals
            (students, questions, tutor conversations, spaced-retry volume) were
            competitive intelligence — anyone could poll them over time and read
            our growth and cost curves off the deltas. The stories are the proof;
            they don't need a scoreboard. Removed 2026-08-02. */}

        {loading && <div className="t-status">{en('common.loading')}</div>}
        {error && <div className="t-status t-status-error">{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className="t-status">{en('testimonials.empty')}</div>
        )}

        <div className="t-list">
          {items.map((t) => (
            <Post key={t.id} t={t} liked={liked.has(t.id)} onLike={() => toggleLike(t)} onShare={(p) => doShare(t, p)} />
          ))}
        </div>

        <section className="t-community">
          <p>Join the conversation — share your story and cheer others on.</p>
          <div className="t-community-links">
            <button className="t-tg-btn" onClick={() => openLink('https://t.me/lomicommunity')}>
              <TgIcon /> Community group
            </button>
            <button className="t-tg-btn secondary" onClick={() => openLink('https://t.me/lomistudy')}>
              <TgIcon /> @lomistudy channel
            </button>
            <button className="t-tg-btn tiktok" onClick={() => openLink('https://tiktok.com/@lomistudy')}>
              <TtIcon /> TikTok
            </button>
          </div>
        </section>
      </main>

      {/* Persistent floating CTA — always within reach so students can weigh in
          right away without scrolling back to the top. */}
      <button className="t-fab" onClick={() => setShowAdd(true)}>
        ＋ {en('testimonials.share')}
      </button>

      {toast && <div className="t-toast">{toast}</div>}
      {!embedded && <footer className="t-footer"><p>🍋 Lomi · Built in Ethiopia</p></footer>}

      {showAdd && <AddTestimonialModal onClose={() => setShowAdd(false)} onSubmitted={load} />}
    </div>
  );
};

export default Testimonials;
